import React from 'react';
import { Settings, Clock, MapPin, Euro } from 'lucide-react';
import { TimePeriod, CustomPeriodMultipliers, CustomPointValues, PERIOD_LABELS, PERIOD_MULTIPLIER_LIMITS } from '../../types';
import { POINT_VALUE_METROPOLE, POINT_VALUE_DROM } from '../../../../utils/pricingUtils';

interface PeriodAndMultiplierSettingsProps {
  selectedPeriod: TimePeriod;
  onPeriodChange: (period: TimePeriod) => void;
  isDrom: boolean;
  onDromChange: (isDrom: boolean) => void;
  customMultipliers: CustomPeriodMultipliers;
  onMultiplierChange: (period: TimePeriod, value: number) => void;
  customPointValues: CustomPointValues;
  onPointValueChange: (zone: 'metropole' | 'drom', value: number) => void;
  darkMode: boolean;
  onValidateChanges?: () => void;
}

const PeriodAndMultiplierSettings: React.FC<PeriodAndMultiplierSettingsProps> = ({
  selectedPeriod, onPeriodChange, isDrom, onDromChange,
  customMultipliers, onMultiplierChange, customPointValues, onPointValueChange,
  darkMode, onValidateChanges
}) => {
  const periods: TimePeriod[] = ['day', 'week', 'month', 'year'];

  const hasChanges = React.useMemo(() => {
    return customMultipliers.week !== 5 || customMultipliers.month !== 20 || customMultipliers.year !== 240 ||
      customPointValues.metropole !== POINT_VALUE_METROPOLE || customPointValues.drom !== POINT_VALUE_DROM;
  }, [customMultipliers, customPointValues]);

  const btnSelected = 'bg-appines-petrol text-appines-white';
  const btnDefault = darkMode
    ? 'bg-appines-white/10 text-appines-white/65 hover:bg-appines-white/15'
    : 'bg-[rgba(1,42,54,0.06)] text-appines-petrol/65 hover:bg-[rgba(1,42,54,0.10)]';
  const labelClass = darkMode ? 'text-appines-white/65' : 'text-appines-petrol/65';
  const subLabel = darkMode ? 'text-appines-white/45' : 'text-appines-petrol/45';
  const inputClass = (disabled = false) => `w-full px-3 py-2 border rounded focus:ring-2 focus:ring-appines-emerald/25 focus:border-appines-emerald ${
    disabled
      ? darkMode ? 'bg-appines-white/5 border-appines-white/10 text-appines-white/35 cursor-not-allowed' : 'bg-[rgba(1,42,54,0.04)] border-[rgba(1,42,54,0.1)] text-appines-petrol/35 cursor-not-allowed'
      : darkMode ? 'bg-appines-petrol border-appines-white/20 text-appines-white' : 'bg-appines-white border-[rgba(1,42,54,0.2)] text-appines-black'
  }`;

  return (
    <div className={`rounded-lg border p-6 ${darkMode ? 'bg-appines-petrol border-appines-white/12' : 'bg-appines-white border-[rgba(1,42,54,0.12)]'}`}>
      <div className="space-y-6">
        <div>
          <label className={`block text-sm font-medium mb-3 ${labelClass}`}>
            <Clock className="h-4 w-4 inline mr-1" /> Période d'affichage des revenus
          </label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {periods.map(period => (
              <button key={period} onClick={() => onPeriodChange(period)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${selectedPeriod === period ? btnSelected : btnDefault}`}>
                {PERIOD_LABELS[period]}
              </button>
            ))}
          </div>
        </div>

        <div>
          <h4 className={`text-sm font-medium mb-3 ${labelClass}`}>
            <Settings className="h-4 w-4 inline mr-1" /> Jours travaillés par période
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {periods.map(period => {
              const limits = PERIOD_MULTIPLIER_LIMITS[period];
              return (
                <div key={period}>
                  <label className={`block text-xs mb-1 ${subLabel}`}>{PERIOD_LABELS[period]}</label>
                  <input type="number" min={limits.min} max={limits.max}
                    value={customMultipliers[period]}
                    onChange={(e) => { const v = Math.max(limits.min, Math.min(limits.max, parseInt(e.target.value) || limits.min)); onMultiplierChange(period, v); }}
                    disabled={period === 'day'}
                    className={`${inputClass(period === 'day')} text-center`}
                  />
                </div>
              );
            })}
          </div>
          <p className={`text-xs mt-2 ${subLabel}`}>Ajustez le nombre de jours travaillés selon votre planning</p>
        </div>

        <div>
          <label className={`block text-sm font-medium mb-3 ${labelClass}`}>
            <MapPin className="h-4 w-4 inline mr-1" /> Zone géographique
          </label>
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => onDromChange(false)} className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${!isDrom ? btnSelected : btnDefault}`}>Métropole</button>
            <button onClick={() => onDromChange(true)} className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${isDrom ? btnSelected : btnDefault}`}>DROM</button>
          </div>
        </div>

        <div>
          <h4 className={`text-sm font-medium mb-3 ${labelClass}`}>
            <Euro className="h-4 w-4 inline mr-1" /> Valeur du point
          </h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={`block text-xs mb-1 ${subLabel}`}>Métropole (€)</label>
              <div className="relative">
                <input type="number" min="0.01" max="10" step="0.01"
                  value={customPointValues.metropole}
                  onChange={(e) => { const v = Math.max(0.01, Math.min(10, parseFloat(e.target.value) || POINT_VALUE_METROPOLE)); onPointValueChange('metropole', v); }}
                  className={inputClass()}
                />
                {customPointValues.metropole !== POINT_VALUE_METROPOLE && (
                  <button onClick={() => onPointValueChange('metropole', POINT_VALUE_METROPOLE)}
                    className={`absolute right-2 top-1/2 -translate-y-1/2 text-xs px-2 py-1 rounded ${darkMode ? 'bg-appines-white/10 text-appines-white/65 hover:bg-appines-white/15' : 'bg-[rgba(1,42,54,0.06)] text-appines-petrol/65 hover:bg-[rgba(1,42,54,0.10)]'}`}
                    title="Remettre la valeur par défaut">Reset</button>
                )}
              </div>
            </div>
            <div>
              <label className={`block text-xs mb-1 ${subLabel}`}>DROM (€)</label>
              <div className="relative">
                <input type="number" min="0.01" max="10" step="0.01"
                  value={customPointValues.drom}
                  onChange={(e) => { const v = Math.max(0.01, Math.min(10, parseFloat(e.target.value) || POINT_VALUE_DROM)); onPointValueChange('drom', v); }}
                  className={inputClass()}
                />
                {customPointValues.drom !== POINT_VALUE_DROM && (
                  <button onClick={() => onPointValueChange('drom', POINT_VALUE_DROM)}
                    className={`absolute right-2 top-1/2 -translate-y-1/2 text-xs px-2 py-1 rounded ${darkMode ? 'bg-appines-white/10 text-appines-white/65 hover:bg-appines-white/15' : 'bg-[rgba(1,42,54,0.06)] text-appines-petrol/65 hover:bg-[rgba(1,42,54,0.10)]'}`}
                    title="Remettre la valeur par défaut">Reset</button>
                )}
              </div>
            </div>
          </div>
          <p className={`text-xs mt-2 ${subLabel}`}>Si le gouvernement se décide à augmenter le coefficient pour une digne rémunération</p>
        </div>

        {hasChanges && onValidateChanges && (
          <div className={`pt-4 border-t ${darkMode ? 'border-appines-white/12' : 'border-[rgba(1,42,54,0.12)]'}`}>
            <button onClick={onValidateChanges}
              className="w-full flex items-center justify-center px-6 py-3 bg-appines-emerald hover:bg-[#22b886] text-appines-petrol font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105">
              <Settings className="h-5 w-5 mr-2" /> Appliquer les nouveaux paramètres
            </button>
            <p className={`text-xs mt-2 text-center ${subLabel}`}>Les calculs seront mis à jour avec vos nouveaux paramètres</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PeriodAndMultiplierSettings;
